import { DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe, JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { Docket, HireItem } from 'src/app/models';
import { HireItemService } from 'src/app/shared';
import { HlCurrencyPipe } from 'src/app/shared/pipes/hl-currency.pipe';

interface DialogData {
  docket: Docket;
  hireItems: HireItem[];
  total: number;
}

@Component({
  selector: 'app-round-price-dialog',
  standalone: true,
  imports: [JsonPipe, HlCurrencyPipe, AsyncPipe, MatDialogModule, MatButton],
  template: `
    <h1 mat-dialog-title>Round Price</h1>
    <div mat-dialog-content>
      <div class="flex flex-col gap-y-8">
        <p>
          The total price of this docket is
          <strong>{{
            data.total
              | hlCurrency
                : data.docket.currency_code
                : data.docket.currency_rate
              | async
          }}</strong
          >.
        </p>
        <p>
          To round the price to the nearest whole number, you can either
          increase a price by
          <strong>{{
            increaseAmount
              | hlCurrency
                : data.docket.currency_code
                : data.docket.currency_rate
              | async
          }}</strong>
          or decrease a price by
          <strong>{{
            decreaseAmount
              | hlCurrency
                : data.docket.currency_code
                : data.docket.currency_rate
              | async
          }}</strong
          >.
        </p>
        <p>
          When you press "Increase" or "Decrease" below we will change the price
          of <strong>{{ firstHireItem?.item.code }}</strong> to result in the
          new total.
        </p>
        <div class="flex flex-row justify-center items-center gap-x-6">
          <button mat-flat-button (click)="mutation.mutate('increase')">
            Increase
          </button>
          <button mat-flat-button (click)="mutation.mutate('decrease')">
            Decrease
          </button>
        </div>
      </div>
    </div>
    <div mat-dialog-actions align="end">
      <button mat-flat-button (click)="dialogRef.close()">Cancel</button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HireItemService]
})
export class RoundPriceDialogComponent {
  dialogRef = inject(MatDialogRef);
  data: DialogData = inject(MAT_DIALOG_DATA);
  hireItemService = inject(HireItemService);
  increaseAmount: number;
  decreaseAmount: number;
  firstHireItem: HireItem;

  mutation = injectMutation((_) => ({
    mutationFn: (action: 'increase' | 'decrease') => {
      const charge = this.getNewCharge(action);
      return lastValueFrom(
        this.hireItemService.update(
          this.firstHireItem.id,
          this.firstHireItem.docket_id,
          { charge }
        )
      );
    },
    onSuccess: () => this.dialogRef.close(true)
  }));

  constructor() {
    console.log(this.data);
    this.dialogRef.updateSize('400px');
    this.determineAmountNeededToRound();
    this.getFirstHireItemWithPrice();
  }
  determineAmountNeededToRound() {
    const total =
      Math.round(this.data.total * this.data.docket.currency_rate) / 100;
    this.increaseAmount = Math.round(
      ((Math.ceil(total) - total) / this.data.docket.currency_rate) * 100
    );
    this.decreaseAmount = Math.round(
      ((Math.floor(total) - total) / this.data.docket.currency_rate) * -100
    );
    console.log({
      total,
      increaseAmount: this.increaseAmount,
      decreaseAmount: this.decreaseAmount
    });
  }

  getFirstHireItemWithPrice() {
    this.firstHireItem = this.data.hireItems.find(
      (item) => item.charge !== null && item.deleted === 0
    );
  }

  getAmountExcludingVat(amount: number) {
    return amount / (this.data.docket.tax_rate + 1);
  }

  getNewCharge(action: 'increase' | 'decrease'): number {
    // 1 for increase, -1 for decrease
    const operator = action === 'increase' ? 1 : -1;
    // Get the value that should be added to the current charge (including VAT)
    const value =
      (action === 'increase' ? this.increaseAmount : this.decreaseAmount) *
      operator;
    // Add the value excluding vat to the charge
    const newCharge =
      Math.round(
        this.firstHireItem.charge * 100 + this.getAmountExcludingVat(value)
      ) / 100;

    // return the new charge adjusted for currency
    return Math.round(newCharge * 100) / 100;
  }
}
